import classNames from "classnames";
import React, {useEffect, useState} from "react";

import {Input} from "@/components/input/Input.tsx";

interface TimeInputProps {
  name: string;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onTimeChange?: (value: string) => void;
}

export const TimeInput = (props: TimeInputProps) => {
  // Format an initial time string to HH:mm (trim seconds if necessary)
  const formatTime = (t: string): string => {
    return t && t.length >= 5 ? t.substring(0, 5) : t;
  };

  const [time, setTime] = useState(formatTime(props.value || props.defaultValue || ""));

  useEffect(() => {
    if (props.value) {
      const formatted = formatTime(props.value);
      if (formatted !== time) {
        setTime(formatted);
      }
    }
  }, [props.value]);

  // Helper to validate partial time input.
  // This function ensures that if the hour is "24", then the minute part must be empty or start with "0"
  const isValidPartialTime = (value: string): boolean => {
    if (value === "") return true;

    const parts = value.split(":");
    if (parts.length > 2) return false;

    // Validate hour part
    const hour = parts[0];
    if (!/^\d{0,2}$/.test(hour)) return false;
    if (hour.length === 2) {
      const hr = parseInt(hour, 10);
      if (hr > 24) return false;
    }

    // If there's no colon yet, we're only editing the hour part.
    if (parts.length === 1) return true;

    // Validate minute part
    const minute = parts[1];
    if (!/^\d{0,2}$/.test(minute)) return false;
    // If hour is "24", the only valid minute is "00"
    if (hour === "24") {
      // Allow no minute digit or only a leading zero.
      if (minute !== "" && minute !== "0" && minute !== "00") {
        return false;
      }
    } else {
      // For hours less than 24, if minute is complete, it must be 0-59.
      if (minute.length === 2) {
        const minVal = parseInt(minute, 10);
        if (minVal > 59) return false;
      }
    }
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove any character except digits and colon
    value = value.replace(/[^0-9:]/g, "");

    // Auto-insert colon when needed (e.g., "1234" becomes "12:34")
    if (!value.includes(":") && value.length > 2) {
      value = value.slice(0, 2) + ":" + value.slice(2);
    }

    // Limit input to maximum 5 characters (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    // Check if the current input is a valid partial time
    if (!isValidPartialTime(value)) {
      return; // Do not update the state if invalid
    }

    // For complete input (5 characters), enforce full validation (including 24:00)
    const validTimeRegex = /^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/;
    if (value.length === 5 && !validTimeRegex.test(value)) {
      return;
    }

    setTime(value);
    if (props.onTimeChange) {
      props.onTimeChange(value);
    }
  };

  return (
    <div className="form-group">
      <Input
        type="text"
        name={props.name}
        className={classNames("form-control")}
        disabled={props.disabled || false}
        readOnly={props.readOnly || false}
        placeholder={props.placeholder || "HH:mm"}
        value={time}
        onChange={handleChange}
      />
    </div>
  );
};
